import Phaser from "phaser";
import { Player } from "../models/player";
import { MapHandler, MapHandlerConfig } from "../handlers/map-handler";
import { TILE_SIZE, LootBoxInfo, MerchantInfo, getLootBoxSpriteInfo } from "../models/common";
import { trackEvent } from "../utils/ga-events";
import { playSound } from "../utils/sound-utils";
import { showBeamUpParticle } from "../ui/splash-particle";
import { updateDamageTexts } from "../ui/damage-text";
import { LootBox } from "../models/loot-box";
import { Merchant } from "../models/merchant";
import { getLootBagSprite } from "../utils/sprite-utils";
import { registerLootBoxListeners, unregisterLootBoxListeners, registerInventoryAndPlayerListeners, unregisterInventoryAndPlayerListeners, registerItemListeners, unregisterItemListeners, registerPlayerListeners, unregisterPlayerListeners, updatePlayerDataAndRefreshInventory } from "../network";
import { BaseGameScene } from "./base-game-scene";
import { updateLootBoxHighlighting } from "../utils/scene-utils";
import { setupTouchControls } from "../controls/controls-touch";
import { setupDesktopControls } from "../controls/controls-desktop";
import { isTouchDevice } from "../utils/mobile-utils";
import { preloadPhaserAssets } from "../sprite-loader";
import { DailyRewardScreen } from "../ui/daily-reward-screen";
import { createFireflyEffect } from "../effects/firefly-effect";
import { createCloudEffect, CloudEffect } from "../effects/cloud-effect";
import { COLORS } from "../ui/colors";
import { ModernButton } from "../ui/modern-button";

export class MarketScene extends BaseGameScene {
  private lootBags: Phaser.GameObjects.Container[] = [];
  private merchants: Merchant[] = [];
  private blockLayer?: Phaser.Tilemaps.TilemapLayer;
  private effectLayer?: Phaser.Tilemaps.TilemapLayer;
  private playerDataListener?: (playerData: any) => void;
  private sceneInitialized: boolean = false;
  private dailyRewardScreen?: DailyRewardScreen;
  private dailySpinOutline?: Phaser.GameObjects.Graphics;
  private dailySpinHighlighted: boolean = false;
  private highlightedMerchant: Merchant | null = null;
  private marketMusic?: Phaser.Sound.BaseSound;
  private escKeyListener?: (event: KeyboardEvent) => void;
  private disconnectListener?: () => void;

  // Ambient effects
  private fireflyEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private cloudEffect?: CloudEffect;

  // Credits HUD
  private creditsHudBg?: Phaser.GameObjects.Graphics;
  private creditsHudIcon?: Phaser.GameObjects.Sprite;
  private creditsHudText?: Phaser.GameObjects.Text;

  constructor() {
    super("MarketScene");
    
    // Initialize MapHandler for marketplace map
    const mapConfig: MapHandlerConfig = {
      mapKey: "marketplace",
      mapJSONPath: "/maps/marketplace.json",
      tilesets: [
        {
          name: "furniture-toxic",
          imageKey: "furniture-toxic",
          imagePath: "/sprites/map/furniture-toxic.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "plants",
          imageKey: "plants",
          imagePath: "/sprites/map/plants.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "Lakes",
          imageKey: "Lakes",
          imagePath: "/sprites/map/lakes.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-office",
          imageKey: "furniture-office",
          imagePath: "/sprites/map/furniture-office.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "house",
          imageKey: "house",
          imagePath: "/sprites/map/house.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-fancy",
          imageKey: "furniture-fancy",
          imagePath: "/sprites/map/furniture-fancy.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-shelter",
          imageKey: "furniture-shelter",
          imagePath: "/sprites/map/furniture-shelter.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "grass",
          imageKey: "grass",
          imagePath: "/sprites/map/grass.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-messy",
          imageKey: "furniture-messy",
          imagePath: "/sprites/map/furniture-messy.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "key-objects",
          imageKey: "key-objects",
          imagePath: "/sprites/map/key-objects.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "town-tiles",
          imageKey: "town-tiles",
          imagePath: "/sprites/map/town-tiles.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "Mansion Furniture",
          imageKey: "Mansion Furniture",
          imagePath: "/sprites/map/furniture-hotel.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "sand",
          imageKey: "sand",
          imagePath: "/sprites/map/sand.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
      ],
    };
    
    this.mapHandler = new MapHandler(this, mapConfig);
  }

  preload() {
    // Load all core game assets (including player spritesheet)
    preloadPhaserAssets(this);

    if (this.mapHandler) {
      this.mapHandler.preload();
    }

    // Preload dust spritesheet for sprint effect
    this.load.spritesheet("dust", "/sprites/effects/dust.png", {
      frameWidth: 16,
      frameHeight: 16,
    });

    // Preload marketplace music
    this.load.audio("marketplace", "/music/marketplace.mp3");

    // Preload merchant spritesheets
    this.load.spritesheet("enemies/merchants", "/sprites/enemies/merchants.png", {
      frameWidth: 32,
      frameHeight: 32,
    });

    // Preload daily reward screen assets
    DailyRewardScreen.preload(this);
    this.load.image("daily-bg", "/sprites/daily-bg.jpeg");
  }

  create() {
    // Wire Phaser's shutdown event to our own shutdown()/stop() overrides — without this,
    // marketCleanup() (and its music/listener teardown) only runs via the manual call sites
    // below, not on any other scene-stop path.
    this.events.once("shutdown", this.shutdown, this);

    // Reset transition flag in case scene is reused (scene.stop doesn't destroy the instance)
    this.isTransitioning = false;

    // Track GA event: player enters marketplace
    trackEvent("enter-marketplace");

    // Get player data from registry
    const playerState = this.registry.get("playerState");
    if (!playerState) {
      console.warn("[MarketScene] No player state found, returning to base");
      this.scene.stop("MarketScene");
      // @ts-ignore
      if (window.addBaseSceneToGame) {
        window.addBaseSceneToGame();
      }
      this.scene.start("BaseScene", {
        playerData: { fromDeath: false },
      });
      return;
    }

    console.log("[MarketScene] Creating scene with player state:", playerState);

    // Create the map
    if (this.mapHandler) {
      this.mapHandler.create();
      this.blockLayer = this.mapHandler.getLayer("tile-objects-block");
      this.effectLayer = this.mapHandler.getLayer("tiles-effect");
    }

    // Create the player at entrance position (43 * TILE_SIZE, 41 * TILE_SIZE)
    const playerId = window.socket?.id || "marketplace-player";
    // Preserve the previously loaded player data (credits, equipment, skin, etc.) —
    // the Player constructor resets data to defaults (credits: 0, ...), so it must
    // be carried over before window.player is replaced with the fresh instance.
    const previousPlayerData = window.player?.data;
    this.player = new Player(playerId, this, 43 * TILE_SIZE, 41 * TILE_SIZE);
    if (previousPlayerData) {
      this.player.data = { ...this.player.data, ...previousPlayerData };
    }
    window.player = this.player; // Update global player reference

    // Apply active skin if available
    if (this.player.data.activeSkin) {
      this.player.updateSkin(this.player.data.activeSkin);
    }

    // Apply equipped weapon if available
    if (this.player.data.equipment?.Weapon) {
      this.player.updateWeaponSprite();
    }

    // Apply equipped hat if available
    this.player.updateHatSprite();

    console.log("[MarketScene] Player created at:", {
      x: 43 * TILE_SIZE,
      y: 41 * TILE_SIZE,
      tileX: 43,
      tileY: 41,
    });

    // Show beam up effect when player appears
    showBeamUpParticle(
      this,
      this.player.container.x,
      this.player.container.y,
    );

    // Create the active teleport sprite at exit location (23, 41) as a permanent indicator
    this.renderActiveTeleport();

    // Create UI Layer for windows
    this.setupUILayer();

    // Initialize daily spin screen and tile outline at (33, 41)
    this.dailyRewardScreen = new DailyRewardScreen(this);
    this.dailyRewardScreen.setWindowManager(this.windowManager!);
    this.dailySpinOutline = this.add.graphics();
    this.dailySpinOutline.lineStyle(3, 0xffffff, 1);
    this.dailySpinOutline.strokeRect(33 * TILE_SIZE, 41 * TILE_SIZE, TILE_SIZE, TILE_SIZE);
    this.dailySpinOutline.setVisible(false);
    this.dailySpinOutline.setDepth(1);
    if (this.uiCamera) {
      this.uiCamera.ignore(this.dailySpinOutline);
    }

    // Show weapon UI in marketplace
    this.windowManager?.showWeaponUI();

    this.windowManager?.hidePlayerWidget();

    // Set up camera to follow player
    const mapWidthPixels = this.mapHandler?.getMapWidthPixels() || this.mapWidth * TILE_SIZE;
    const mapHeightPixels = this.mapHandler?.getMapHeightPixels() || this.mapHeight * TILE_SIZE;
    
    this.setupCamera(mapWidthPixels, mapHeightPixels);

    // Show credits HUD at top center
    this.createCreditsHUD();

    // Use desktop or touch controls with localOnly flag
    if (isTouchDevice()) {
      setupTouchControls(this, false, false);
    } else {
      setupDesktopControls(this, false);
    }

    // EXIT button (replaces sprint on touch; adds to desktop too)
    const exitButtonX = this.scale.width - 120;
    const exitButtonY = this.scale.height - 40;
    const exitButton = new ModernButton({
      scene: this,
      x: exitButtonX,
      y: exitButtonY,
      text: "EXIT",
      style: "danger",
      onClick: () => this.returnToBase(),
    });
    exitButton.setScrollFactor(0);
    exitButton.setDepth(10099);
    if (this.uiLayer) {
      this.uiLayer.add(exitButton);
    }

    // Set up socket listeners BEFORE emitting join-market
    // (race condition: server responds immediately, listeners must be ready)
    this.setupMarketSocketListeners();

    // Emit join-market to server
    if (window.socket) {
      window.socket.emit("join-market");

      // Send initial position immediately after join-market so other players see our position
      // Use a small delay to ensure join-market is processed first
      setTimeout(() => {
        if (window.socket && this.player) {
          this.lastSentPosition.x = this.player.x;
          this.lastSentPosition.y = this.player.y;
          window.socket.emit("market-player-position", {
            x: this.player.x,
            y: this.player.y,
          });
        }
      }, 10);
    }

    // Start position update loop
    this.startPositionUpdateLoop("market-player-position");

    // Play marketplace music (stop any leftover instance from a prior visit first)
    if (this.marketMusic) {
      this.marketMusic.stop();
      this.marketMusic = undefined;
    }
    if (this.cache.audio.exists("marketplace") && localStorage.getItem("musicEnabled") !== "false") {
      this.marketMusic = this.sound.add("marketplace", { volume: 0.4, loop: true });
      this.marketMusic.play();
    }

    // Mark scene as initialized so listeners can process events
    this.sceneInitialized = true;

    // Create ambient firefly effect
    const mapW = this.mapHandler?.getMapWidthTiles() ?? 64;
    const mapH = this.mapHandler?.getMapHeightTiles() ?? 64;
    this.fireflyEmitter = createFireflyEffect(this, mapW, mapH);

    // Create ambient cloud drift effect
    this.cloudEffect = createCloudEffect(this);
  }

  private setupMarketSocketListeners() {
    if (!window.socket) return;

    // Initial market state
    window.socket.on(
      "market-state",
      (data: {
        playersInMarket: number;
        players?: Array<{ id: string; x: number; y: number; accountName: string; activeSkin?: string; weapon?: any; equippedHat?: number }>;
        lootbags: any[];
        merchants: MerchantInfo[];
        mapName: string;
      }) => {
        console.log("[MarketScene] Initial market state:", data);

        // Create other players from initial state
        if (data.players && data.players.length > 0) {
          data.players.forEach((p) => {
            if (p.id === window.socket.id) {
              return; // Ignore self
            }

            // Create player if not already created
            if (!this.otherPlayers[p.id]) {
              this.setupMarketPlayer(p.id, {
                position: { x: p.x, y: p.y },
                accountName: p.accountName,
                activeSkin: p.activeSkin,
                weapon: p.weapon,
                equippedHat: p.equippedHat,
              });
            }
          });
        }

        // Load initial lootbags
        if (data.lootbags) {
          this.loadLootbags(data.lootbags);
        }

        // Load merchants
        if (data.merchants) {
          this.loadMerchants(data.merchants);
        }
      },
    );

    // Handle ESC key to return to base
    if (this.escKeyListener) {
      window.removeEventListener("keydown", this.escKeyListener);
    }
    this.escKeyListener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        this.returnToBase();
      }
    };
    window.addEventListener("keydown", this.escKeyListener);

    // Register player syncing listeners (handles market-player-entered, market-player-moved, market-player-left)
    registerPlayerListeners(this, {
      otherPlayers: this.otherPlayers,
      uiCamera: this.uiCamera,
      adapterType: "market",
    });

    // Player disconnected
    window.socket.on("market-player-disconnected", (data: { socketId: string }) => {
      this.removeOtherPlayer(data.socketId);
    });

    // Handle disconnect from market (return to base)
    if (this.disconnectListener) {
      window.socket.off("disconnect", this.disconnectListener);
    }
    this.disconnectListener = () => {
      this.marketCleanup();
      // @ts-ignore
      if (window.addBaseSceneToGame) {
        window.addBaseSceneToGame();
      }
      this.scene.start("BaseScene", {
        playerData: { fromDeath: false },
      });
    };
    window.socket.on("disconnect", this.disconnectListener);

    // Register item/loot-bag syncing listeners
    registerItemListeners(this, this.windowManager);

    // Register loot box listeners for pickup/interaction
    registerLootBoxListeners(this, this.windowManager!);

    // Market-specific player-data listener for inventory window refresh
    // This must be registered BEFORE the global inventory listeners to ensure
    // inventory window is refreshed when items are dropped
    this.playerDataListener = (playerData: any) => {
      if (!this.sceneInitialized || !this.windowManager) return;
      updatePlayerDataAndRefreshInventory(playerData, this.windowManager);
      this.creditsHudText?.setText(`${this.player?.data?.credits ?? 0} Credits`);
    };
    window.socket.on("player-data", this.playerDataListener);

    // Register inventory and player data listeners for other UI updates (shops, crafting, etc.)
    // but skip the inventory window update since we handle it above
    registerInventoryAndPlayerListeners(this, this.windowManager!);
  }

  private setupMarketPlayer(socketId: string, data: any) {
    // Use base class method to create the player
    const otherPlayer = this.addOtherPlayer(socketId, data.position.x, data.position.y, 100);

    // Set the account name from the data
    if (data.accountName) {
      otherPlayer.data.name = data.accountName;
      otherPlayer.healthbar?.setName(data.accountName);
    }

    // Apply active skin if provided
    if (data.activeSkin) {
      otherPlayer.data.activeSkin = data.activeSkin;
      otherPlayer.updateSkin(data.activeSkin);
    }

    // Set weapon equipment if provided
    if (data.weapon) {
      if (!otherPlayer.data.equipment) {
        otherPlayer.data.equipment = {};
      }
      otherPlayer.data.equipment.Weapon = data.weapon;
      otherPlayer.updateWeaponSprite();
    }

    // Apply equipped hat if provided
    otherPlayer.data.equippedHat = data.equippedHat;
    otherPlayer.updateHatSprite();

    // Show beam up effect when other player appears
    showBeamUpParticle(this, otherPlayer.container.x, otherPlayer.container.y);

    console.log(
      `[MarketScene] Added other player ${data.accountName || socketId} at (${data.position.x}, ${data.position.y})`,
    );
  }

  private loadLootbags(lootbags: any[]) {
    // Initialize lootBoxes array if needed
    if (!this.mapHandler?.lootBoxes) {
      this.mapHandler!.lootBoxes = [];
    }

    // Add initial lootbags
    lootbags.forEach((lootbagData: LootBoxInfo | any) => {
      // Handle both old format (with id, position) and new format (with x, y directly)
      const lootBoxInfo: LootBoxInfo = lootbagData.x !== undefined 
        ? lootbagData 
        : { x: Math.floor(lootbagData.position.x / TILE_SIZE), y: Math.floor(lootbagData.position.y / TILE_SIZE), items: lootbagData.items || [] };

      // Check if already exists
      const existing = this.mapHandler!.lootBoxes!.find(
        (lb: LootBox) => lb.info.x === lootBoxInfo.x && lb.info.y === lootBoxInfo.y,
      );
      if (existing) {
        return; // Skip if already exists
      }

      const lootBagSpriteInfo = getLootBoxSpriteInfo(lootBoxInfo);
      const py = lootBoxInfo.y * TILE_SIZE + TILE_SIZE / 2;
      const lootBagSprite = getLootBagSprite(this, lootBagSpriteInfo);
      lootBagSprite.setDepth(1000 + py);
      if (this.uiCamera) {
        this.uiCamera.ignore(lootBagSprite);
      }
      const lootBox = new LootBox(lootBoxInfo, lootBagSprite);
      lootBox.createBlueprintBackground(this);
      this.mapHandler!.lootBoxes!.push(lootBox);
    });
  }

  private loadMerchants(merchantsData: MerchantInfo[]) {
    this.merchants = [];

    merchantsData.forEach((merchantData: MerchantInfo) => {
      // Create merchant sprite from spritesheet
      const frameKey = `${merchantData.spriteX},${merchantData.spriteY}`;
      const px = merchantData.x * TILE_SIZE;
      const py = merchantData.y * TILE_SIZE;

      // Create sprite from spritesheet
      const sprite = this.add.image(px, py, "enemies/merchants");
      sprite.setFrame(merchantData.spriteY * 2 + merchantData.spriteX); // 2 columns per row
      sprite.setOrigin(0, 0);
      const depth = 1000 + py;
      sprite.setDepth(depth);

      if (this.uiCamera) {
        this.uiCamera.ignore(sprite);
      }

      // Create merchant instance
      const merchant = new Merchant(merchantData, sprite);
      this.merchants.push(merchant);
    });

    console.log(`[MarketScene] Loaded ${this.merchants.length} merchants`);
  }

  private updateMerchantHighlighting(playerTileX: number, playerTileY: number) {
    if (!this.merchants || this.merchants.length === 0 || !this.windowManager) {
      // Hide merchant button if no merchants
      this.highlightedMerchant = null;
      this.windowManager?.hideMerchantButton?.();
      return;
    }

    // Find nearest merchant that is adjacent to player
    let nearestMerchant: Merchant | null = null;
    let minDistance = Infinity;

    for (const merchant of this.merchants) {
      if (merchant.isPlayerAdjacent(playerTileX, playerTileY)) {
        const distance =
          Math.abs(merchant.info.x - playerTileX) +
          Math.abs(merchant.info.y - playerTileY);
        
        if (distance < minDistance) {
          minDistance = distance;
          nearestMerchant = merchant;
        }
      }
    }

    // Update highlighting
    if (nearestMerchant !== this.highlightedMerchant) {
      const previousMerchant = this.highlightedMerchant;

      // Hide outline from previous merchant
      if (previousMerchant) {
        previousMerchant.hideOutline();
      }

      this.highlightedMerchant = nearestMerchant;

      if (nearestMerchant) {
        // Show outline for new merchant
        nearestMerchant.renderOutline(this);

        // Play highlight sound, same as when highlighting containers/stations
        playSound(this, "highlight");

        // Show TRADE button
        this.windowManager?.showMerchantButton?.(this, nearestMerchant);
      } else {
        // Hide button when no merchant nearby
        this.windowManager?.hideMerchantButton?.();

        // Close the merchant window if the player walked away from the merchant it belongs to
        if (
          previousMerchant &&
          this.windowManager?.isMerchantWindowVisible?.() &&
          this.windowManager.getMerchantWindowMerchantId?.() === previousMerchant.info.id
        ) {
          this.windowManager.hideMerchantWindow();
        }
      }
    }
  }

  private addLootbag(id: string, position: { x: number; y: number }, itemCount: number) {
    // Create lootbag visual (simple sprite for now)
    const bag = this.add.container(position.x, position.y);

    // Lootbag sprite
    const sprite = this.add.sprite(0, 0, "loot-bag");
    sprite.setOrigin(0.5);
    sprite.setDepth(1000 + position.y);

    // Item count text
    if (itemCount > 0) {
      const text = this.add.text(0, 0, itemCount.toString(), {
        fontSize: "12px",
        color: "#fff",
        align: "center",
      });
      text.setOrigin(0.5);
      text.setDepth(1001);
      bag.add(text);
    }

    bag.add(sprite);
    bag.setData("lootBoxId", id);
    this.lootBags.push(bag);
  }

  private updateLootbagVisuals(lootBoxId: string, itemCount: number) {
    const bag = this.lootBags.find((b) => b.getData("lootBoxId") === lootBoxId);
    if (bag) {
      // Update text count if needed
      const text = bag.getByName("itemCount");
      if (text && itemCount > 0) {
        (text as Phaser.GameObjects.Text).setText(itemCount.toString());
      }
    }
  }

  private removeLootbag(lootBoxId: string) {
    const index = this.lootBags.findIndex((b) => b.getData("lootBoxId") === lootBoxId);
    if (index !== -1) {
      const bag = this.lootBags[index];
      this.tweens.add({
        targets: bag,
        alpha: 0,
        scale: 0.5,
        duration: 200,
        onComplete: () => {
          bag.destroy();
        },
      });
      this.lootBags.splice(index, 1);
    }
  }

  private renderActiveTeleport() {
    // Render the active teleport sprite at exit location (23, 41)
    const teleportX = 23 * TILE_SIZE + TILE_SIZE / 2;
    const teleportY = 41 * TILE_SIZE + TILE_SIZE / 2;

    // Wait for teleport-active texture to be loaded
    if (!this.textures.exists("teleport-active")) {
      // Queue the render for when textures load
      this.load.once("complete", () => {
        this.renderActiveTeleport();
      });
      return;
    }

    // Create animation if it doesn't exist
    if (!this.anims.exists("teleport-active-anim")) {
      this.anims.create({
        key: "teleport-active-anim",
        frames: this.anims.generateFrameNumbers("teleport-active", {
          start: 0,
          end: 1,
        }),
        frameRate: 4,
        repeat: -1,
      });
    }

    // Create the animated teleport sprite
    const teleportSprite = this.add.sprite(
      teleportX,
      teleportY,
      "teleport-active"
    );
    teleportSprite.play("teleport-active-anim");
    // Set depth so it renders above tiles but below player
    const effectsLayer = this.mapHandler?.getLayer("tiles-effect");
    const effectDepth = effectsLayer ? effectsLayer.depth : 5;
    teleportSprite.setDepth(effectDepth);

    // Store reference for cleanup if needed
    (this as any).activeTeleportSprite = teleportSprite;
  }

  update() {
    if (!this.player || !this.mapHandler) return;

    // Interpolate other players to their target positions
    const deltaSeconds = this.game.loop.delta / 1000;
    this.interpolateOtherPlayers(deltaSeconds);

    // Check for teleport detection (within ~1 tile radius = 32 pixels)
    const detectionRadius = 32;
    const playerTileX = Math.floor(this.player.container.x / TILE_SIZE);
    const playerTileY = Math.floor(this.player.container.y / TILE_SIZE);

    // Exit teleport at (23, 41) - returns to BaseScene
    if (
      !this.isTransitioning &&
      Math.abs(this.player.container.x - 23 * TILE_SIZE) < detectionRadius &&
      Math.abs(this.player.container.y - 41 * TILE_SIZE) < detectionRadius
    ) {
      console.log("[MARKETPLACE] Exit teleport detected at (23, 41)");
      this.returnToBase();
    }

    // Update loot box highlighting
    updateLootBoxHighlighting(this, this.player, this.windowManager, this.mapHandler?.lootBoxes);

    // Update merchant highlighting
    this.updateMerchantHighlighting(playerTileX, playerTileY);

    // Update daily spin interactable highlighting
    this.updateDailySpinHighlighting(playerTileX, playerTileY);

    // Update damage texts and status labels animation
    updateDamageTexts(this);

    // Tick cloud drift — hide when player is indoors
    if (this.cloudEffect) {
      const isIndoors = !!(this.player && this.mapHandler &&
        this.mapHandler.isIndoorTile(this.player.x, this.player.y));
      this.cloudEffect.setVisible(!isIndoors);
      this.cloudEffect.update(this.game.loop.delta);
    }

    // Check roof visibility based on player position
    if (this.player && this.mapHandler) {
      this.mapHandler.checkRoofVisibility(this.player.x, this.player.y);
    }
  }

  shutdown() {
    this.marketCleanup();
    super.shutdown();
  }

  stop() {
    this.marketCleanup();
    super.stop();
  }

  /**
   * Cleanup method called when transitioning away from the market
   */
  private cleanup() {
    this.marketCleanup();
  }

  private marketCleanup() {
    // Mark scene as not initialized to prevent processing stale events
    this.sceneInitialized = false;

    // Remove socket listeners
    if (window.socket) {
      window.socket.off("market-state");
      window.socket.off("market-player-entered");
      window.socket.off("market-player-moved");
      window.socket.off("add-loot-bag");
      window.socket.off("remove-loot-box");
      window.socket.off("update-loot-bag-sprite");
      window.socket.off("market-player-left");
      window.socket.off("market-player-disconnected");
      
      // Unregister the specific playerDataListener
      if (this.playerDataListener) {
        window.socket.off("player-data", this.playerDataListener);
      }

      // Unregister the disconnect listener
      if (this.disconnectListener) {
        window.socket.off("disconnect", this.disconnectListener);
        this.disconnectListener = undefined;
      }

      // Notify server we're leaving market
      window.socket.emit("leave-market");
    }

    // Remove ESC key listener
    if (this.escKeyListener) {
      window.removeEventListener("keydown", this.escKeyListener);
      this.escKeyListener = undefined;
    }

    // Unregister inventory and loot box listeners
    unregisterInventoryAndPlayerListeners();
    unregisterPlayerListeners("market");
    unregisterItemListeners();

    // Clean up fog overlay via map handler
    if (this.mapHandler) {
      this.mapHandler.destroyFogOverlay();
    }

    // Unregister loot box listeners
    unregisterLootBoxListeners();

    // Stop marketplace music
    if (this.marketMusic) {
      this.marketMusic.stop();
      this.marketMusic = undefined;
    }

    // Destroy firefly emitter
    if (this.fireflyEmitter) {
      this.fireflyEmitter.destroy();
      this.fireflyEmitter = undefined;
    }

    // Destroy cloud effect
    if (this.cloudEffect) {
      this.cloudEffect.destroy();
      this.cloudEffect = undefined;
    }

    // Clear lootbags
    this.lootBags = [];

    // Hide merchant button and clear merchants
    this.windowManager?.hideMerchantButton?.();
    this.merchants.forEach((merchant) => merchant.remove());
    this.merchants = [];
    this.highlightedMerchant = null;

    // Destroy credits HUD
    this.destroyCreditsHUD();

    // Clean up daily spin screen and outline
    if (this.dailyRewardScreen?.isVisible()) {
      this.dailyRewardScreen.hide();
    }
    this.dailySpinOutline?.destroy();
    this.dailySpinOutline = undefined;
    this.dailySpinHighlighted = false;
  }

  private updateDailySpinHighlighting(playerTileX: number, playerTileY: number) {
    const distance = Math.abs(playerTileX - 33) + Math.abs(playerTileY - 41);
    const inRange = distance <= 1;

    if (inRange && !this.dailySpinHighlighted) {
      this.dailySpinHighlighted = true;
      this.dailySpinOutline?.setVisible(true);
      playSound(this, "highlight");
      this.windowManager?.showOpenButton(this, () => this.openDailySpin(), "DAILY SPIN");
    } else if (!inRange && this.dailySpinHighlighted) {
      this.dailySpinHighlighted = false;
      this.dailySpinOutline?.setVisible(false);
      this.windowManager?.hideOpenButton();
    }
  }

  private openDailySpin() {
    if (this.marketMusic && this.marketMusic.isPlaying) {
      this.marketMusic.pause();
    }
    this.dailyRewardScreen?.show({
      showWheel: true,
      showNoneSlots: false,
      socketEvent: "request-daily-spin",
      title: "DAILY SPIN",
      bgKey: "daily-bg",
      onClose: () => {
        if (this.marketMusic && !this.marketMusic.isPlaying) {
          (this.marketMusic as Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound).resume();
        }
      },
    });
  }

  private createCreditsHUD(): void {
    const credits = (window as any).player?.data?.credits ?? 0;
    const cx = this.scale.width / 2;
    const y = 28;

    this.creditsHudText = this.add.text(0, 0, `${credits} Credits`, {
      fontSize: "16px",
      fontFamily: "Anton",
      color: COLORS.YELLOW,
      shadow: { offsetX: 0, offsetY: 2, color: "#000000", blur: 0, stroke: true, fill: true },
    });
    this.creditsHudText.setScrollFactor(0);
    this.creditsHudText.setOrigin(0, 0.5);

    const iconSize = 16;
    const gap = 16;
    const textW = this.creditsHudText.width;
    const totalW = iconSize + gap + textW;
    const startX = cx - totalW / 2;

    this.creditsHudText.setPosition(startX + iconSize + gap, y);

    this.creditsHudIcon = this.add.sprite(startX + iconSize / 2, y, "items", 202);
    this.creditsHudIcon.setScrollFactor(0);

    const padX = 12;
    const padY = 8;
    const bgX = startX - padX;
    const bgY = y - 8 - padY;
    const bgW = totalW + padX * 2;
    const bgH = 16 + padY * 2;

    this.creditsHudBg = this.add.graphics();
    this.creditsHudBg.fillStyle(0x000000, 0.65);
    this.creditsHudBg.fillRoundedRect(bgX, bgY, bgW, bgH, 6);
    this.creditsHudBg.lineStyle(2, 0x666666, 1);
    this.creditsHudBg.strokeRoundedRect(bgX, bgY, bgW, bgH, 6);
    this.creditsHudBg.setScrollFactor(0);

    if (this.uiLayer) {
      this.uiLayer.add([this.creditsHudBg, this.creditsHudIcon, this.creditsHudText]);
    }
  }

  private destroyCreditsHUD(): void {
    this.creditsHudBg?.destroy();
    this.creditsHudBg = undefined;
    this.creditsHudIcon?.destroy();
    this.creditsHudIcon = undefined;
    this.creditsHudText?.destroy();
    this.creditsHudText = undefined;
  }

  private returnToBase() {
    // Guard against multiple calls
    if (this.isTransitioning) {
      return;
    }
    this.isTransitioning = true;
    
    console.info("[MarketScene] Returning to base");
    
    if (this.player) {
      // Play teleport sound and particle effect
      playSound(this, "teleport");
      
      // Show beam up particle effect at player position
      showBeamUpParticle(
        this,
        this.player.container.x,
        this.player.container.y,
      );
      
      // Hide player sprite immediately so only teleport effect is shown
      this.player.container.setVisible(false);
    }
    
    // Prepare transition data
    const self = this;
    
    // Wait for animation to play before transitioning (800ms for full animation)
    this.time.delayedCall(800, () => {
      // Do cleanup
      self.marketCleanup();
      console.log("GO TO BASE SCENE");
      // Ensure BaseScene is added to the game
      // @ts-ignore
      if (window.addBaseSceneToGame) {
        window.addBaseSceneToGame();
      }
      // Stop this scene and restart BaseScene with playerData
      self.scene.stop("MarketScene");
      self.scene.start("BaseScene", {
        playerData: { fromDeath: false },
      });
    });
  }

  // Returns true if the given world (pixel) coordinates are blocked
  // Uses collision detection from the block layer
  isBlocked(x: number, y: number): boolean {
    const tileX = Math.floor(x / TILE_SIZE);
    const tileY = Math.floor(y / TILE_SIZE);
    
    const tile = this.blockLayer?.getTileAt(tileX, tileY);
    const tileBlocked =
      tile !== null &&
      tile !== undefined &&
      tile.index !== -1 &&
      tile.index !== 0;

    // Check containers layer
    const containersLayer = this.mapHandler?.getLayer("containers");
    let containerBlocked = false;
    if (containersLayer) {
      const containerTile = containersLayer.getTileAt(tileX, tileY);
      containerBlocked =
        containerTile !== null &&
        containerTile !== undefined &&
        containerTile.index !== -1 &&
        containerTile.index !== 0;
    }

    return tileBlocked || containerBlocked;
  }

  // Check for teleport tiles (not used in marketplace, but needed for desktop controls)
  checkEffectTile(x: number, y: number): boolean {
    // Market scene doesn't have teleport tiles, always return false
    return false;
  }
}
